import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert
} from '@mui/material';
import { Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { blobticService } from '../../Commun/services/blobticService';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// Interface pour un paramètre
interface SysParameter {
    Code: string;
    Value: string;
    Type: string;
    Libelle: string;
    LastUpdate: string;
    UserId: number;
}

const ParametersPage: React.FC = () => {
    const { user } = useAuth();
    
    const { t } = useLanguage();

    const [parameters, setParameters] = useState<SysParameter[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // États pour l'édition
    const [editOpen, setEditOpen] = useState(false);
    const [currentParam, setCurrentParam] = useState<SysParameter | null>(null);
    const [editValue, setEditValue] = useState('');

    // Chargement des paramètres
    const fetchParameters = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await blobticService.executeZoomRequest<SysParameter[]>({
                ZoomKey: 'sys_parameters',
                Action: {
                    OKey: 'Blobtic_Parameters,BlobticParameters,get_all'
                }
            });

            if (response.success) {
                setParameters(response.data);
            } else {
                setError(response.message || t('page.parameters.error.load'));
            }
        } catch (err: any) {
            console.error('Erreur chargement paramètres:', err);
            setError(err.message || t('page.parameters.error.load'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchParameters();
    }, []);

    // Ouverture du dialogue d'édition
    const handleEdit = (param: SysParameter) => {
        setCurrentParam(param);
        setEditValue(param.Value);
        setEditOpen(true);
    };

    // Sauvegarde des modifications
    const handleSave = async () => {
        if (!currentParam) return;

        try {
            const response = await blobticService.executeZoomRequest<any>({
                ZoomKey: 'sys_parameters_update',
                Action: {
                    OKey: 'Blobtic_Parameters,BlobticParameters,update',
                    SYSPAR_CODE: currentParam.Code,
                    SYSPAR_VALUE: editValue
                }
            });

            if (response.success) {
                blobticMessage.success(t('page.parameters.success.title'), t('page.parameters.success.message', currentParam.Code));
                setEditOpen(false);
                fetchParameters(); // Recharger la liste
            } else {
                blobticMessage.error(t('page.parameters.error.title'), response.message || t('page.parameters.error.update'));
            }
        } catch (err: any) {
            console.error('Erreur mise à jour:', err);
            blobticMessage.error(t('page.parameters.error.title'), t('page.parameters.error.update'));
        }
    };

    return (
        <Container maxWidth="xl" sx={{ height: '100%', pt: 2, pb: 2, display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{
                p: 2,
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderRadius: 2
            }}>
                <Box>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                        {t('page.parameters.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('page.parameters.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchParameters}
                    disabled={isLoading}
                    size="small"
                >
                    {t('page.parameters.action.refresh')}
                </Button>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer sx={{ flexGrow: 1 }}>
                        <Table stickyHeader aria-label="table des paramètres">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default' }}>{t('page.parameters.table.code')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default' }}>{t('page.parameters.table.label')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default' }}>{t('page.parameters.table.value')}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.default', display: { xs: 'none', md: 'table-cell' } }}>{t('page.parameters.table.type')}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.default' }}>{t('page.parameters.table.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parameters.map((row) => (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.Code}>
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                                {row.Code}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{row.Libelle || '-'}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 300,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {row.Value}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            <Box sx={{
                                                display: 'inline-block',
                                                px: 1,
                                                py: 0.5,
                                                bgcolor: 'action.hover',
                                                borderRadius: 1,
                                                fontSize: '0.75rem'
                                            }}>
                                                {row.Type}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleEdit(row)} color="primary" size="small" aria-label={t('page.parameters.action.edit')}>
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {parameters.length === 0 && !isLoading && !error && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            {t('page.parameters.table.empty')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Dialogue d'édition */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {t('page.parameters.dialog.title', currentParam?.Code)}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {t('page.parameters.dialog.description')}
                        </Typography>
                        <Typography variant="body2" paragraph>
                            {currentParam?.Libelle || '-'}
                        </Typography>

                        <TextField
                            autoFocus
                            margin="dense"
                            id="param-value"
                            label={t('page.parameters.dialog.value')}
                            type="text"
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            helperText={`Type: ${currentParam?.Type}`}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)} color="inherit">
                        {t('page.parameters.dialog.cancel')}
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {t('page.parameters.dialog.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ParametersPage;
